import { onlineManager } from '@tanstack/react-query';

import { getWipeEpoch } from './connection';
import { getOfflineReadCache, getOfflineWriteCache, parseCachedPayload } from './hydration';
import { deleteJsonRows, listJsonRows, upsertJsonRows } from './repositories/core';
import { getSyncState, setSyncState } from './repositories/sync-state';

import type { OfflineDbExecutor } from './driver-types';
import type { CachedRow, JsonRowListTable } from './repositories/core';

export interface OfflineListCacheOptions<T extends NonNullable<unknown>> {
  readonly ownerUserId: string | undefined;
  readonly organizationId: string;
  /**
   * The cached family. A single table literal rather than an injected
   * statement set: three separately-passed functions could be assembled from
   * two different tables, publishing one family's view signature over the
   * other's rows.
   */
  readonly table: JsonRowListTable;
  /** sync_state scope - one per cached family (`tasks`, `notifications`). */
  readonly scope: string;
  /**
   * Identity of the cached VIEW within the scope: filters, page window, and a
   * stamp of the cached row SHAPE. Built with `stableStringify` by callers
   * whose view is parameterised; a constant for callers whose view is not.
   * Nothing else distinguishes two lists sharing a family's rows, so a
   * signature that omits a dimension serves one view's rows as another's.
   */
  readonly viewSignature: string;
  /** Row key. An empty key abandons the write - see {@link toCachedRows}. */
  readonly getRowId: (item: T) => string;
  readonly fetchFromServer: () => Promise<T[]>;
  /**
   * Veto for writes AND cached reads. Keeps an empty or incomplete result
   * from clobbering a previously good list - offline, that cached empty would
   * render as a convincing "nothing here" where "you are offline" is the
   * truth - and makes a cached list the veto rejects read as a MISS. It is
   * also where a caller checks the cached SHAPE, since the payload column is
   * opaque and `parseCachedPayload` cannot type-check what it returns.
   */
  readonly shouldCache?: (items: T[]) => boolean;
  /**
   * Reproduce the server's ordering on the cache read. These families store
   * one row per record with no ordering column, so SQLite returns them in an
   * order of its choosing. Callers pass this NEXT TO the `.order(...)` clause
   * it mirrors, and the two move together.
   */
  readonly compareCached?: (left: T, right: T) => number;
  /** TanStack's abort signal: a superseded fetch must not win the rows. */
  readonly signal?: AbortSignal;
  readonly nowMs?: number;
}

export type OfflineListCacheReadOptions<T extends NonNullable<unknown>> = Omit<
  OfflineListCacheOptions<T>,
  'fetchFromServer' | 'getRowId' | 'signal'
>;

/*
 * `T extends NonNullable<unknown>` is a guard, not decoration: a legitimately
 * null item is indistinguishable from the MISS `parseCachedPayload` returns
 * for a corrupt payload, so a nullable item type would let real nulls vanish
 * from a list read back as complete. The sibling payload cache documents the
 * same hazard; here the constraint makes it a compile error.
 */

/**
 * Read-through / write-through caching for a LIST of rows stored per-row.
 *
 * The sibling of `withOfflinePayloadCache`, for families whose natural
 * unit is a row rather than one heavy JSON blob: tasks and notifications live
 * in their own tables, outside the body-cache byte budget, and are already
 * covered by the tenancy purge. Everything else is deliberately identical -
 * same guard set, same failure posture, same MISS semantics.
 *
 * The view-signature discipline is what makes a per-row store safe to read
 * back as a list. One signature's rows occupy the family at a time; a write
 * invalidates, purges, repopulates, then re-publishes the signature. Two
 * consequences, both deliberate: a view that was never cached reports a MISS
 * rather than an empty list (the caller then fails honestly instead of
 * showing a convincing but wrong "all clear"), and two views cannot blend.
 * Concurrent consumers of DIFFERENT views of one family therefore evict each
 * other - the cost is offline availability for whichever wrote last, never a
 * wrong list.
 *
 * "Never a wrong list" needs BOTH halves of the concurrency guard, because
 * neither the driver nor SQLite serialises anything for us: writes to one
 * family run one at a time (see {@link runSerialized}, or two interleaved
 * purge/repopulate passes publish a signature over a blend of both), and the
 * read brackets its rows between two cursor checks (or a write landing
 * between them returns the new view's rows under the old view's identity).
 *
 * Both directions are failure-tolerant. The cache is disposable: a read that
 * throws is a MISS, a write that throws costs offline availability, and
 * neither may fail the query the caller is actually waiting on.
 *
 * There is NO read-side access-clock touch here, because these families sit
 * outside the byte budget and have no eviction order to protect. The one
 * clock they do own - `sync_state.last_synced_at` - is deliberately left
 * alone on a cache hit: restamping it would claim a sync that never happened.
 * A future byte-budgeted family adding a touch must earn it by ACCEPTANCE
 * (after parse and veto) and keep it best-effort, as the payload cache does.
 *
 * The two halves stay asymmetric by design: `getOfflineReadCache` is locked
 * until a verified sync stamps `markVerified`, while the write half populates
 * regardless - which is what gives the stamp something to unlock, and what
 * lets a device that re-verifies have something fresh to show for it.
 */
export async function withOfflineListCache<T extends NonNullable<unknown>>(
  options: OfflineListCacheOptions<T>,
): Promise<T[]> {
  const { organizationId, scope, viewSignature, shouldCache, signal, nowMs, fetchFromServer } =
    options;

  // A cache entry without its full identity is a leak vector, not a cache: an
  // empty organizationId would create an un-scoped bucket readable under
  // whichever org is active, and an empty scope or signature would collide
  // every caller's rows into one bucket. Guarded HERE so no future caller has
  // to remember it.
  if (!organizationId || !scope || !viewSignature) {
    return await fetchFromServer();
  }

  // Captured before ANY await, the cached read included: opening the cache can
  // itself await an in-flight wipe and then re-create the database, so an
  // epoch read afterwards would already be the post-wipe one and every later
  // comparison would agree with it - exactly the resurrection this guards.
  const epochAtEntry = getWipeEpoch();

  if (!onlineManager.isOnline()) {
    const cached = await readAcceptableList(options);
    if (cached !== null) {
      return cached;
    }
    // Fall through deliberately: with nothing cached the fetch fails, and an
    // honest error beats fabricated content.
  }

  let fresh: T[];
  try {
    fresh = await fetchFromServer();
  } catch (error) {
    // The connection dropped DURING the fetch - the normal mobile failure
    // (lift, tunnel, car park). Entry-time online meant the cache was never
    // consulted; consult it now. A failure while STILL online stays a real
    // failure: falling back there would mask server errors with old content.
    if (!onlineManager.isOnline()) {
      const cached = await readAcceptableList(options);
      if (cached !== null) {
        return cached;
      }
    }
    throw error;
  }

  const cacheable = shouldCache === undefined || shouldCache(fresh);
  if (cacheable && !isAborted(signal) && getWipeEpoch() === epochAtEntry) {
    // Fresh timestamp for the write: the entry-time clock would stamp
    // synced_at before the fetch even ran, skewing cache age by its duration.
    await writeList(options, fresh, nowMs ?? Date.now(), epochAtEntry);
  }
  return fresh;
}

/** Fresh read of the abort flag - it flips DURING awaits. */
function isAborted(signal: AbortSignal | undefined): boolean {
  return signal?.aborted === true;
}

/** The cached list, unless the signature, the parse, or the veto disowns it. */
async function readAcceptableList<T extends NonNullable<unknown>>(
  options: OfflineListCacheReadOptions<T>,
): Promise<T[] | null> {
  const {
    ownerUserId,
    organizationId,
    table,
    scope,
    viewSignature,
    shouldCache,
    compareCached,
    nowMs,
  } = options;
  try {
    const cache = await getOfflineReadCache(ownerUserId, organizationId, nowMs ?? Date.now());
    if (!cache) {
      return null;
    }
    // MISS, never an empty list: the family may hold another view's rows, or
    // none at all, and both must fall through to the network.
    if (!(await cursorMatches(cache, organizationId, scope, viewSignature))) {
      return null;
    }
    const rows = await listJsonRows(cache, table, organizationId);
    const items: T[] = [];
    for (const row of rows) {
      const item = parseCachedPayload<T>(row.payload);
      if (item === null) {
        // A list is read back as COMPLETE, so one unreadable row disowns the
        // whole list rather than silently shortening it - the mirror of the
        // write's all-or-nothing serialisation.
        return null;
      }
      items.push(item);
    }
    // Re-checked AFTER the rows are in hand: the cursor read and the row read
    // are two statements, and a write landing between them would otherwise
    // hand back the NEW view's rows under the old view's identity.
    if (!(await cursorMatches(cache, organizationId, scope, viewSignature))) {
      return null;
    }
    if (compareCached) {
      items.sort(compareCached);
    }
    if (shouldCache !== undefined && !shouldCache(items)) {
      return null;
    }
    return items;
  } catch {
    return null;
  }
}

/**
 * Read one verified encrypted list without starting its server request.
 * Cold-start bootstrap uses this to seed TanStack before observers mount;
 * their normal queryFns then reconcile the stale seed in the background.
 */
export async function readOfflineListCache<T extends NonNullable<unknown>>(
  options: OfflineListCacheReadOptions<T>,
): Promise<T[] | null> {
  return await readAcceptableList(options);
}

async function cursorMatches(
  cache: OfflineDbExecutor,
  organizationId: string,
  scope: string,
  viewSignature: string,
): Promise<boolean> {
  const state = await getSyncState(cache, organizationId, scope);
  return state?.cursor === viewSignature;
}

/**
 * One write at a time per cached family.
 *
 * The write is four separate bridge round-trips (invalidate, purge,
 * repopulate, publish) and neither the driver nor the plugin serialises them,
 * so two concurrent writes to one family interleave: A purges, B purges, B
 * repopulates and publishes, A repopulates and publishes - leaving A's
 * signature over a blend of both views, which reads back as a valid list. The
 * chain is keyed per (organization, family) so unrelated families still write
 * concurrently.
 */
const writeChains = new Map<string, Promise<void>>();

/**
 * Separator for the per-family serialisation key. U+001F (unit separator)
 * cannot appear in a UUID or a scope literal, so two families cannot
 * collide onto one lock - and unlike a raw NUL it leaves the source
 * greppable and reviewable.
 */
const FAMILY_KEY_SEPARATOR = '\u001F';

async function runSerialized(key: string, work: () => Promise<void>): Promise<void> {
  const previous = writeChains.get(key) ?? Promise.resolve();
  const tail = previous.then(async (): Promise<void> => {
    try {
      await work();
    } catch {
      // Swallowed HERE so the chain stays a promise nobody can reject: a
      // failed write costs offline availability, never the writes queued
      // behind it.
    }
  });
  writeChains.set(key, tail);
  try {
    await tail;
  } finally {
    // Only the last writer clears the entry, or a queued successor would lose
    // its predecessor and run concurrently after all.
    if (writeChains.get(key) === tail) {
      writeChains.delete(key);
    }
  }
}

async function writeList<T extends NonNullable<unknown>>(
  options: OfflineListCacheOptions<T>,
  items: T[],
  nowMs: number,
  epochAtEntry: number,
): Promise<void> {
  const { ownerUserId, organizationId, table, scope, viewSignature, getRowId, signal } = options;
  try {
    // Serialised BEFORE the cache is opened: a list that cannot be stored
    // whole should not cost a database open at all.
    const rows = toCachedRows(items, getRowId);
    if (rows === null) {
      return;
    }
    // FAMILY_KEY_SEPARATOR, not a raw control byte: an embedded NUL makes
    // the file read as binary to grep and is invisible in review.
    await runSerialized(`${organizationId}${FAMILY_KEY_SEPARATOR}${scope}`, async () => {
      const cache = await getOfflineWriteCache(ownerUserId, organizationId);
      if (!cache) {
        return;
      }
      // Both vetoes re-checked AFTER acquisition, which is the check that
      // decides: the open path can AWAIT an in-flight wipe and then re-open
      // the database (epoch), and TanStack can abort a superseded fetch while
      // the open or the queue wait is in flight (signal) - either way the
      // pre-acquisition check alone leaves the rows winnable by a fetch that
      // should have lost.
      if (getWipeEpoch() !== epochAtEntry || isAborted(signal)) {
        return;
      }
      // Invalidate BEFORE touching rows. A failure between the purge and the
      // repopulate would otherwise leave the previous signature still matching
      // a now-empty family - and on a refetch of the SAME view that signature
      // is the one being written, so an offline read would match and return a
      // convincing, wrong, empty list. Clearing the cursor first makes every
      // mid-write failure a cache MISS instead.
      await setSyncState(cache, organizationId, scope, nowMs, '', null);
      await deleteJsonRows(cache, table, organizationId);
      await upsertJsonRows(cache, table, organizationId, rows, nowMs);
      await setSyncState(cache, organizationId, scope, nowMs, '', viewSignature);
    });
  } catch {
    // Disposable cache: losing a write costs offline availability, never the
    // result the user is waiting on.
  }
}

/**
 * Serialise the whole list, or nothing. A single unstorable row abandons the
 * write rather than being dropped: a list is read back as complete, so a
 * silently short one is worse than no cache at all. `JSON.stringify` returns
 * undefined for undefined/function/symbol despite its string return type, and
 * binding that into a NOT NULL column would throw inside a queryFn.
 */
function toCachedRows<T>(items: readonly T[], getRowId: (item: T) => string): CachedRow[] | null {
  const rows: CachedRow[] = [];
  for (const item of items) {
    const id = getRowId(item);
    const payload = JSON.stringify(item);
    if (!id || typeof payload !== 'string') {
      return null;
    }
    rows.push({ id, payload });
  }
  return rows;
}
